import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from 'react-query';
import { fetchPaginatedSites } from 'api/site';

import { useNavigate } from 'react-router-dom';
import { routesSettings } from 'constants/routes';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';

import { Center, Flex, Text } from '@chakra-ui/react';

import { SiteModal } from 'components/modules/SiteModal';
import { DataTable, ServerSort } from 'components/modules/DataTable';

import { Site } from 'types/site';

import { TabProps } from './CustomerSites.props';
import { buildSiteColumns } from './sitesColumns';

type Props = TabProps & {
  /** Distinguishes this tab's cache entry: 'all' | 'active' | … */
  queryScope: string;
  /** 1 = active only, 2 = inactive only; omitted on the All tab. */
  activeOnly?: 1 | 2;
  archived?: 1;
};

/**
 * Shared body of the four Customer Sites tabs — everything but the query
 * scope and the active/archived filter was copy-pasted per tab.
 *
 * Sorting is owned by the parent (sortBy/sort/sortHandler props), so this
 * only mirrors that state into the shape DataTable renders.
 */
const SitesTable: FC<Props> = ({
  search,
  sortBy,
  sort,
  sortHandler,
  queryScope,
  activeOnly,
  archived,
}) => {
  const navigate = useNavigate();
  const addSiteModalRef = useRef<any>(null);

  const [page, setPage] = useState<number>(1);

  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const queryKey = [
    `fetch-sites-${queryScope}-${washHubId}`,
    page,
    search,
    sortBy,
    sort,
  ];

  const queryFn = () =>
    fetchPaginatedSites({
      page,
      limit: 10,
      washHub: washHubId,
      search,
      sortKey: sortBy,
      sort,
      ...(activeOnly !== undefined ? { activeOnly } : {}),
      ...(archived !== undefined ? { archived } : {}),
    } as any);

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };

  const { data, isFetching } = useQuery(queryKey, queryFn, options);

  useWashHubListener(() => {
    setPage(1);
  });

  useEffect(() => {
    setPage(1);
  }, [search, sortBy, sort]);

  const columns = useMemo(() => buildSiteColumns(), []);

  const sites = data?.items || [];

  const pagination = {
    page,
    setPage,
    totalCount: data?.total || 0,
    totalPages: data?.totalPages || 0,
  };

  // The parent owns the sort state; mirror it into DataTable's shape and
  // hand clicks back to its handler.
  const serverSort: ServerSort | undefined =
    sortBy && sort ? { field: sortBy, dir: sort === 1 ? 1 : -1 } : undefined;

  return (
    <Flex direction="column" flex={1}>
      <DataTable<Site>
        minW="1100px"
        data={sites}
        columns={columns}
        isLoading={isFetching}
        pagination={pagination}
        sort={serverSort}
        onSortChange={(next) => sortHandler?.(next.field)}
        onRowClick={(site) => {
          navigate(routesSettings.PORTAL_SITE_DETAILS.getPath(site._id), {
            state: site,
          });
        }}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Sites available</Text>
          </Center>
        )}
      />
      <SiteModal ref={addSiteModalRef} />
    </Flex>
  );
};

export default SitesTable;
